import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Link from 'next/link';

export function PageHero({ title, subtitle, kicker, children, compact = false }: { title: string; subtitle?: string; kicker?: string; children?: React.ReactNode; compact?: boolean; }) {
  return (
  <div className={`relative mb-12 py-[70px]`}>
      {/* Full-bleed background (spans viewport even inside a max-width container) and tucks under header */}
      <div className="absolute left-1/2 top-0 -translate-x-1/2 w-screen h-full -z-10 pt-6">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-[var(--brand-accent-soft)] to-white" />
        {/* Radial glow overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(13,110,253,0.18),transparent_60%)]" />
      </div>
      <div className={`${compact ? 'py-8 md:py-10' : 'py-10 md:py-14'}`}>
        <div className="max-w-4xl">
          {kicker && <div className="text-xs font-semibold tracking-wide uppercase text-blue-600 mb-3">{kicker}</div>}
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">{title}</h1>
          {subtitle && <p className="text-sm/6 md:text-base text-[var(--brand-muted)] max-w-2xl">{subtitle}</p>}
          {children && <div className="mt-6">{children}</div>}
        </div>
      </div>
    </div>
  );
}

export function BulletGrid({ items, cols = 3 }: { items: { title: string; text: string }[]; cols?: 2 | 3 | 4; }) {
  const gridCols = cols === 4 ? 'md:grid-cols-4' : cols === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3';
  return (
    <div className={`grid gap-6 ${gridCols}`}>
      {items.map(i => (
        <div 
          key={i.title} 
          className="p-6 border-2 border-blue-100 rounded-xl bg-white hover:shadow-lg hover:border-blue-200 hover:-translate-y-1 transition-all duration-300"
        >
          <h3 className="text-lg font-semibold mb-3 text-blue-600">{i.title}</h3>
          <p className="text-sm text-gray-700 leading-relaxed">{i.text}</p>
        </div>
      ))}
    </div>
  );
}

export function NumberedSteps({ steps }: { steps: { label: string; text: string }[] }) {
  return (
    <ol className="space-y-4">
      {steps.map((s, idx) => (
        <li key={s.label} className="flex gap-4">
          <div className="size-8 rounded-full bg-blue-600/10 text-blue-700 font-semibold flex items-center justify-center text-xs">{idx + 1}</div>
          <div className="space-y-1">
            <div className="text-sm font-medium">{s.label}</div>
            <p className="text-xs text-[var(--brand-muted)] leading-relaxed">{s.text}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function Callout({ title, body, cta }: { title: string; body: string; cta?: { href: string; label: string } }) {
  return (
    <div className="rounded-2xl border border-[var(--brand-border)] bg-gradient-to-r from-blue-50 to-cyan-50 p-8 space-y-4">
      <h3 className="text-lg font-medium tracking-tight">{title}</h3>
      <p className="text-sm/6 text-[var(--brand-muted)] max-w-2xl">{body}</p>
      {cta && (
        <Link href={cta.href}><Button size="sm">{cta.label}</Button></Link>
      )}
    </div>
  );
}

export function TestimonialCards({ items }: { items: { quote: string; name: string; detail?: string; }[] }) {
  return (
    <div className="grid gap-6 md:grid-cols-3">
      {items.map(t => (
        <Card key={t.quote} className="relative flex flex-col hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
          <CardContent className="pt-6 flex-1 flex flex-col text-sm">
            <p className="italic leading-relaxed flex-1">"{t.quote}"</p>
            {t.detail && <div className="text-[10px] uppercase tracking-wide text-[var(--brand-muted)] mt-4">{t.detail}</div>}
            <div className="text-xs font-medium text-blue-700 mt-4 pt-4 border-t border-gray-100">{t.name}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function TwoColumn({ left, right }: { left: React.ReactNode; right: React.ReactNode }) {
  return (
    <div className="grid gap-10 md:grid-cols-2">
      <div className="space-y-6">{left}</div>
      <div className="space-y-6">{right}</div>
    </div>
  );
}

export function PlaceholderVideo({ label }: { label?: string }) {
  return (
    <div className="aspect-video rounded-lg border border-[var(--brand-border)] bg-[var(--brand-muted-bg)] flex items-center justify-center text-xs text-[var(--brand-muted)]">
      {label || 'Video Placeholder'}
    </div>
  );
}
